// @flow
import * as React from 'react';
import styled from 'styled-components';
import { PrimaryButtonLink } from 'components/Button';
import Box from 'components/Box';
import { RichTextHeadline, H4 } from 'components/Text';
import { FilestackImage } from 'components/Image';
import Link from 'components/Link';
import PlaceholderImage from 'components/PlaceholderImage';
import HideContainer from 'containers/HideContainer';
import { trackClicked } from 'utils/analytics';
import { identity, isNotEmpty } from 'utils/functional';
import type {
  MiniMiniPage,
  FourArticlesHighlightWebsiteData,
  Subsite
} from 'api/website';

import { isBrowser } from 'utils/browser';
import { gutterWidth, bp } from '../../theme';

const PageSection = styled(
  ({
    page,
    pageNum,
    displayImages,
    blockIndex,
    pageUpdated,
    subsite,
    className
  }: {
    page: MiniMiniPage,
    pageNum: number,
    displayImages: boolean,
    blockIndex: number,
    pageUpdated: string,
    subsite: Subsite,
    className: string
  }) => {
    const hasPlaceholder = isNotEmpty(subsite.placeholderImages);
    const hasImage = !!page.image;
    const showPlaceholder = isBrowser() && !hasImage && hasPlaceholder;
    const showImage = hasImage || showPlaceholder;
    return (
      <Box
        width={[1, 1, 1 / 2, 1 / 4]}
        px={[gutterWidth / 4, null, null, gutterWidth / 2]}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent={['center', 'flex-start']}
        className={className}
        flex="0 0 auto"
      >
        {displayImages && (
          <Box
            position="relative"
            textAlign="center"
            mb={[gutterWidth / 4, null, gutterWidth / 2]}
          >
            {showImage ? (
              <Link
                href={`/${page.url}`}
                tabIndex="-1"
                onClick={() => {
                  trackClicked('CMS Block', {
                    block_position: blockIndex,
                    block_type: 'Four Articles Highlight',
                    cta_type: 'Image',
                    page_title: page.title,
                    page_updated: pageUpdated
                  });
                }}
                aria-label={page.title}
              >
                {showPlaceholder ? (
                  <PlaceholderImage
                    postId={page.id}
                    subsite={subsite}
                    alt={page.title.slice(0, 100)}
                    width={190}
                    height={190}
                    data-qa-id={`image-${pageNum}`}
                    round
                    fit="crop"
                    className={className}
                  />
                ) : (
                  <FilestackImage
                    alt={page.title.slice(0, 100)}
                    data={page.image}
                    width={190}
                    height={190}
                    data-qa-id={`image-${pageNum}`}
                    round
                    fit="crop"
                  />
                )}
              </Link>
            ) : (
              <HideContainer noWrapper xs sm>
                <div style={{ height: 190 }} />
              </HideContainer>
            )}
          </Box>
        )}
        <Link
          href={`/${page.url}`}
          onClick={() => {
            trackClicked('CMS Block', {
              block_position: blockIndex,
              block_type: 'Four Articles Highlight',
              cta_type: 'Link',
              page_title: page.title,
              page_updated: pageUpdated
            });
          }}
        >
          <H4 mb={0} lineHeight={1.25}>
            {page.title}
          </H4>
        </Link>
      </Box>
    );
  }
)`
  @media (max-width: ${bp[1] - 1}px) {
    &:not(:first-child) {
      margin-top: ${(gutterWidth * 3) / 4}px;
    }
  }
  @media (min-width: ${bp[1]}px) and (max-width: ${bp[2] - 1}px) {
    &:not(:nth-child(1)):not(:nth-child(2)) {
      margin-top: ${gutterWidth}px;
    }
  }

  a {
    position: relative;
    display: block;
    max-width: 240px;
  }

  img {
    display: block;
  }
`;

const FourArticlesHighlightBlock = ({
  data,
  blockIndex,
  pageUpdated,
  subsite
}: {
  data: FourArticlesHighlightWebsiteData,
  blockIndex: number,
  pageUpdated: string,
  subsite: Subsite
}) => {
  const ARTICLES = ['One', 'Two', 'Three', 'Four']
    .map(page => data[`page${page}`])
    .filter(identity);

  const { headline, buttonText, buttonUrl } = data;
  const displayImages =
    ARTICLES.some(page => page.image) || isNotEmpty(subsite.placeholderImages);
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      width="100%"
    >
      {headline && (
        <RichTextHeadline mb={[gutterWidth / 2, null, null, gutterWidth]}>
          {headline}
        </RichTextHeadline>
      )}
      <Box
        display="flex"
        flexWrap="wrap"
        flex="0 0 auto"
        width="100%"
        mx={[-gutterWidth / 4, null, null, -gutterWidth / 2]}
      >
        {ARTICLES.map((page, index) => (
          <PageSection
            page={page}
            pageNum={index}
            key={page.id}
            displayImages={displayImages}
            blockIndex={blockIndex}
            pageUpdated={pageUpdated}
            subsite={subsite}
          />
        ))}
      </Box>
      {buttonText && buttonUrl && (
        <Box mt={['32px', null, '52px']} width="100%">
          <PrimaryButtonLink href={buttonUrl}>{buttonText}</PrimaryButtonLink>
        </Box>
      )}
    </Box>
  );
};

export default FourArticlesHighlightBlock;
